"use client";

import type { DatePickerProps, DateRangePickerProps } from "@heroui/react";
import {
  Calendar,
  cn,
  DateField,
  DatePicker,
  DateRangePicker,
  Label,
  RangeCalendar,
  TimeField,
} from "@heroui/react";
import type { Time } from "@internationalized/date";
import type { DateValue } from "@internationalized/date";

/** 与 HeroUI `DatePicker` / `DateRangePicker` 的 `granularity` 一致 */
export type DateTimePickerGranularity = "day" | "hour" | "minute" | "second";

/** 12 / 24 小时制（本地时区，不提供时区选择） */
export type DateTimePickerHourCycle = "12-hour" | "24-hour";

export type DateTimePickerMode = "single" | "range";

type PickerGranularityProps = "granularity" | "hideTimeZone" | "hourCycle" | "shouldForceLeadingZeros";

type SharedDateTimePickerFieldProps = {
  /** 默认 `single` */
  mode?: DateTimePickerMode;
  /** 默认 `day` */
  granularity?: DateTimePickerGranularity;
  /** 默认 `12-hour` */
  hourCycle?: DateTimePickerHourCycle;
  /** 对应 HeroUI `shouldForceLeadingZeros`，默认 `false` */
  forceLeadingZeros?: boolean;
  /** 传给 `DateField.Group`（单区间与区间模式均支持） */
  fullWidth?: boolean;
  /** 日历 Popover 内 `Calendar` / `RangeCalendar` 的无障碍标签 */
  calendarAriaLabel?: string;
  /** `DatePicker.Popover` / `DateRangePicker.Popover` 的 className */
  popoverClassName?: string;
};

export type DateTimePickerFieldSingleProps = SharedDateTimePickerFieldProps & {
  mode?: "single";
} & Omit<DatePickerProps<DateValue>, "children" | PickerGranularityProps>;

export type DateTimePickerFieldRangeProps = SharedDateTimePickerFieldProps & {
  mode: "range";
} & Omit<DateRangePickerProps<DateValue>, "children" | PickerGranularityProps>;

export type DateTimePickerFieldProps = DateTimePickerFieldSingleProps | DateTimePickerFieldRangeProps;

function toHeroHourCycle(cycle: DateTimePickerHourCycle | undefined): 12 | 24 {
  return cycle === "24-hour" ? 24 : 12;
}

/**
 * 日期时间：分段输入 + 日历图标打开 Popover（与 HeroUI DatePicker / DateRangePicker 演示结构一致）。
 * 始终使用用户本地时区（`hideTimeZone` 固定为 `true`），不提供时区选择。
 */
export function DateTimePickerField(props: DateTimePickerFieldProps) {
  const granularity = props.granularity ?? "day";
  const hourCycle = toHeroHourCycle(props.hourCycle);
  const shouldForceLeadingZeros = props.forceLeadingZeros ?? false;
  const fullWidth = props.fullWidth;
  const timeGranularity = granularity !== "day" ? granularity : undefined;
  const showTimeField = Boolean(timeGranularity);

  if (props.mode === "range") {
    const {
      mode: _mode,
      granularity: _g,
      hourCycle: _h,
      forceLeadingZeros: _f,
      fullWidth: _fw,
      calendarAriaLabel,
      popoverClassName,
      className,
      ...pickerProps
    } = props;

    const ariaLabel = calendarAriaLabel ?? "选择日期区间";

    return (
      <DateRangePicker
        className={cn(fullWidth && "w-full", className)}
        granularity={granularity}
        hideTimeZone
        hourCycle={hourCycle}
        shouldForceLeadingZeros={shouldForceLeadingZeros}
        {...pickerProps}
      >
        {({ state }) => (
          <>
            <DateField.Group fullWidth={fullWidth} variant="secondary">
              <DateField.InputContainer>
                <DateField.Input slot="start">
                  {(segment) => <DateField.Segment segment={segment} />}
                </DateField.Input>
                <DateRangePicker.RangeSeparator />
                <DateField.Input slot="end">
                  {(segment) => <DateField.Segment segment={segment} />}
                </DateField.Input>
              </DateField.InputContainer>
              <DateField.Suffix>
                <DateRangePicker.Trigger>
                  <DateRangePicker.TriggerIndicator />
                </DateRangePicker.Trigger>
              </DateField.Suffix>
            </DateField.Group>

            <DateRangePicker.Popover className={popoverClassName ?? "flex w-full flex-col gap-3"}>
              <RangeCalendar aria-label={ariaLabel} className="w-full">
                <RangeCalendar.Header>
                  <RangeCalendar.YearPickerTrigger>
                    <RangeCalendar.YearPickerTriggerHeading />
                    <RangeCalendar.YearPickerTriggerIndicator />
                  </RangeCalendar.YearPickerTrigger>
                  <RangeCalendar.NavButton slot="previous" />
                  <RangeCalendar.NavButton slot="next" />
                </RangeCalendar.Header>
                <RangeCalendar.Grid>
                  <RangeCalendar.GridHeader>
                    {(day) => <RangeCalendar.HeaderCell>{day}</RangeCalendar.HeaderCell>}
                  </RangeCalendar.GridHeader>
                  <RangeCalendar.GridBody>
                    {(date) => <RangeCalendar.Cell date={date} />}
                  </RangeCalendar.GridBody>
                </RangeCalendar.Grid>
                <RangeCalendar.YearPickerGrid>
                  <RangeCalendar.YearPickerGridBody>
                    {({ year }) => <RangeCalendar.YearPickerCell year={year} />}
                  </RangeCalendar.YearPickerGridBody>
                </RangeCalendar.YearPickerGrid>
              </RangeCalendar>

              {showTimeField ? (
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-sm">开始时间</Label>
                    <TimeField
                      aria-label="开始时间"
                      granularity={timeGranularity}
                      hideTimeZone
                      hourCycle={hourCycle}
                      name="rangeStartTime"
                      shouldForceLeadingZeros={shouldForceLeadingZeros}
                      value={state.timeRange?.start ?? null}
                      onChange={(v) =>
                        state.setTimeRange({
                          end: state.timeRange?.end as unknown as Time,
                          start: v as unknown as Time,
                        })
                      }
                    >
                      <TimeField.Group variant="secondary">
                        <TimeField.Input>
                          {(segment) => <TimeField.Segment segment={segment} />}
                        </TimeField.Input>
                      </TimeField.Group>
                    </TimeField>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-sm">结束时间</Label>
                    <TimeField
                      aria-label="结束时间"
                      granularity={timeGranularity}
                      hideTimeZone
                      hourCycle={hourCycle}
                      name="rangeEndTime"
                      shouldForceLeadingZeros={shouldForceLeadingZeros}
                      value={state.timeRange?.end ?? null}
                      onChange={(v) =>
                        state.setTimeRange({
                          end: v as unknown as Time,
                          start: state.timeRange?.start as unknown as Time,
                        })
                      }
                    >
                      <TimeField.Group variant="secondary">
                        <TimeField.Input>
                          {(segment) => <TimeField.Segment segment={segment} />}
                        </TimeField.Input>
                      </TimeField.Group>
                    </TimeField>
                  </div>
                </div>
              ) : null}
            </DateRangePicker.Popover>
          </>
        )}
      </DateRangePicker>
    );
  }

  const {
    mode: _mode,
    granularity: _g,
    hourCycle: _h,
    forceLeadingZeros: _f,
    fullWidth: _fw,
    calendarAriaLabel,
    popoverClassName,
    className,
    ...pickerProps
  } = props as DateTimePickerFieldSingleProps;

  const ariaLabel = calendarAriaLabel ?? "选择日期";

  return (
    <DatePicker
      className={cn(fullWidth && "w-full", className)}
      granularity={granularity}
      hideTimeZone
      hourCycle={hourCycle}
      shouldForceLeadingZeros={shouldForceLeadingZeros}
      {...pickerProps}
    >
      {({ state }) => (
        <>
          <DateField.Group fullWidth={fullWidth} variant="secondary">
            <DateField.Input>{(segment) => <DateField.Segment segment={segment} />}</DateField.Input>
            <DateField.Suffix>
              <DatePicker.Trigger>
                <DatePicker.TriggerIndicator />
              </DatePicker.Trigger>
            </DateField.Suffix>
          </DateField.Group>

          <DatePicker.Popover className={popoverClassName ?? "flex flex-col gap-3"}>
            <Calendar aria-label={ariaLabel}>
              <Calendar.Header>
                <Calendar.YearPickerTrigger>
                  <Calendar.YearPickerTriggerHeading />
                  <Calendar.YearPickerTriggerIndicator />
                </Calendar.YearPickerTrigger>
                <Calendar.NavButton slot="previous" />
                <Calendar.NavButton slot="next" />
              </Calendar.Header>
              <Calendar.Grid>
                <Calendar.GridHeader>
                  {(day) => <Calendar.HeaderCell>{day}</Calendar.HeaderCell>}
                </Calendar.GridHeader>
                <Calendar.GridBody>{(date) => <Calendar.Cell date={date} />}</Calendar.GridBody>
              </Calendar.Grid>
              <Calendar.YearPickerGrid>
                <Calendar.YearPickerGridBody>
                  {({ year }) => <Calendar.YearPickerCell year={year} />}
                </Calendar.YearPickerGridBody>
              </Calendar.YearPickerGrid>
            </Calendar>

            {showTimeField ? (
              <div className="flex items-center justify-between gap-2">
                <Label className="text-sm">时间</Label>
                <TimeField
                  aria-label="时间"
                  granularity={timeGranularity}
                  hideTimeZone
                  hourCycle={hourCycle}
                  name="time"
                  shouldForceLeadingZeros={shouldForceLeadingZeros}
                  value={state.timeValue}
                  onChange={(v) => state.setTimeValue(v as unknown as Time)}
                >
                  <TimeField.Group variant="secondary">
                    <TimeField.Input>
                      {(segment) => <TimeField.Segment segment={segment} />}
                    </TimeField.Input>
                  </TimeField.Group>
                </TimeField>
              </div>
            ) : null}
          </DatePicker.Popover>
        </>
      )}
    </DatePicker>
  );
}
